import React, { useEffect, useState } from "react";
import AdminLayout from "../components/AdminLayout";
import { api } from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { CheckCircle, XCircle } from "lucide-react";
import { toast } from "sonner";

export default function AgencyClinicalReview() {
  const [items, setItems] = useState([]);
  const [notes, setNotes] = useState({});

  const load = () => api.get("/agency/production-queue").then(r => {
    setItems(r.data.filter(i => i.status === "review_pending"));
  }).catch(() => {});

  useEffect(() => { load(); }, []);

  const review = async (q, approve) => {
    try {
      await api.post("/agency/clinical-review", {
        queue_id: q.queue_id, approve, notes: notes[q.queue_id] || "",
      });
      toast.success(approve ? "Approved and published." : "Rejected.");
      load();
    } catch { toast.error("Review failed."); }
  };

  return (
    <AdminLayout>
      <div className="haruki-fade-in">
        <h1 className="font-heading text-3xl font-bold">Clinical Review</h1>
        <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
          Approve or reject scenarios queued by the production pipeline. Approved scenarios are published to the library.
        </p>

        <div className="mt-8 space-y-4">
          {items.map(i => (
            <Card key={i.queue_id} data-testid={`review-row-${i.queue_id}`}>
              <CardContent className="p-6">
                <div className="flex flex-wrap items-center gap-3 text-xs">
                  <span className="rounded-full bg-secondary px-2 py-1 capitalize">Review pending</span>
                  <span className="text-muted-foreground">{i.vertical.replace(/_/g, " ")}</span>
                  <span className="text-muted-foreground">· {i.incident_category.replace(/_/g, " ")}</span>
                  <span className="text-muted-foreground">· {i.emotional_beat.replace(/_/g, " ")}</span>
                  <span className="text-muted-foreground">· {i.modality_primary.replace(/_/g, " ")}</span>
                </div>

                <div className="mt-4 grid gap-6 lg:grid-cols-2">
                  <div>
                    <div className="mb-2 text-xs uppercase tracking-widest text-muted-foreground">Script</div>
                    <p className="rounded-md border border-border p-4 text-sm">{i.script}</p>
                    <div className="mt-3 space-y-2">
                      {(i.response_options || []).map(o => (
                        <div key={o.id} className="rounded-md border border-border bg-secondary/30 px-3 py-2 text-xs">
                          {o.text} <span className="text-muted-foreground">— {o.modality}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div className="mb-2 text-xs uppercase tracking-widest text-muted-foreground">Image preview</div>
                    {i.image_url ? (
                      <img
                        src={i.image_url.startsWith("http") ? i.image_url : `${process.env.REACT_APP_BACKEND_URL}${i.image_url}`}
                        alt="" className="aspect-video w-full rounded-md border border-border object-cover"
                      />
                    ) : (
                      <div className="aspect-video rounded-md border border-dashed border-border p-6 text-center text-xs text-muted-foreground">
                        No image generated. Script-only review.
                      </div>
                    )}
                    <div className="mt-3 rounded-md border border-border p-3 text-xs text-muted-foreground">
                      HMDE content score: <strong>{Math.round(i.hmde_content_score || 0)}</strong> · Approved by engine: <strong>{i.content_eval?.approved ? "yes" : "no"}</strong>
                    </div>
                  </div>
                </div>

                <Textarea
                  data-testid={`review-notes-${i.queue_id}`}
                  className="mt-4"
                  placeholder="Reviewer notes (optional, required on rejection)"
                  value={notes[i.queue_id] || ""}
                  onChange={(e) => setNotes({ ...notes, [i.queue_id]: e.target.value })}
                />

                <div className="mt-4 flex gap-3">
                  <Button
                    data-testid={`review-approve-${i.queue_id}`}
                    onClick={() => review(i, true)}
                    style={{ background: "hsl(var(--haruki-leaf))", color: "white" }}
                  >
                    <CheckCircle className="mr-2 h-4 w-4" /> Approve & publish
                  </Button>
                  <Button
                    data-testid={`review-reject-${i.queue_id}`}
                    variant="outline"
                    onClick={() => review(i, false)}
                  >
                    <XCircle className="mr-2 h-4 w-4" /> Reject
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
          {items.length === 0 && <Card><CardContent className="p-6 text-sm text-muted-foreground">No items pending review.</CardContent></Card>}
        </div>
      </div>
    </AdminLayout>
  );
}
